"use client";

import { useMemo, useState } from "react";
import type { Boutique, Product } from "@/lib/products-data";
import { formatPrice } from "@/lib/format";
import { images } from "@/lib/images";
import { productImage } from "@/lib/product-image";
import { useCart } from "@/lib/cart-context";

type SortKey = "default" | "price-asc" | "price-desc" | "name";

interface BoutiqueSectionProps {
  boutique: Boutique;
  index: number;
}

const PALETTE_LIGHT: Record<string, string> = {
  "frais-marche": "#e8f0d8",
  "epicerie-fine": "#f0e4d0",
  "beaute-parfums": "#f5e0e4",
  "hightech-electro": "#e0e8f0",
  "mode-maison": "#f0e0e0",
  "cave-exception": "#f5dde0",
  "bazar-deco": "#f0e4d0",
  "pharmacie": "#e0eee4",
  "boulangerie-royale": "#f5e8c8",
  "comptoir-cafes": "#ecdfd0",
  "epicerie-monde": "#f5e0d0",
  "espace-enfant": "#f0e2d4",
};

export function BoutiqueSection({ boutique, index }: BoutiqueSectionProps) {
  const [sort, setSort] = useState<SortKey>("default");
  // Show all 50 products per boutique by default, as requested.
  const [visible, setVisible] = useState(50);

  const sortedProducts = useMemo<Product[]>(() => {
    const arr = [...boutique.products];
    switch (sort) {
      case "price-asc":
        return arr.sort((a, b) => a.price - b.price);
      case "price-desc":
        return arr.sort((a, b) => b.price - a.price);
      case "name":
        return arr.sort((a, b) => a.name.localeCompare(b.name));
      default:
        return arr;
    }
  }, [boutique, sort]);

  const shown = sortedProducts.slice(0, visible);
  const bgTint = PALETTE_LIGHT[boutique.id] ?? "#f7f1e6";
  const imgKey = `boutique:${boutique.id}` as keyof typeof images;
  const imgSrc = images[imgKey] as string | undefined;

  return (
    <section
      id={boutique.id}
      className="relative py-20 md:py-28 border-t border-ink/8"
      style={{
        background: `linear-gradient(180deg, ${bgTint} 0%, var(--cream) 80%)`,
      }}
    >
      <div className="mx-auto max-w-7xl px-6">
        {/* Header with banner image */}
        <div className="relative mb-12 overflow-hidden rounded-2xl bg-ink text-cream">
          {/* Banner image */}
          <div className="relative h-56 md:h-72 overflow-hidden">
            {imgSrc && (
              <img
                src={imgSrc}
                alt={boutique.name}
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/30"></div>
            <div
              className="absolute inset-0 opacity-30"
              style={{
                backgroundImage: `linear-gradient(135deg, ${boutique.accent}40 0%, transparent 70%)`,
              }}
            ></div>

            {/* Header content overlay */}
            <div className="relative h-full flex flex-col justify-end p-6 md:p-10">
              <div className="flex items-center gap-3 mb-3">
                <span
                  className="font-display italic text-3xl leading-none"
                  style={{ color: boutique.accent }}
                >
                  {boutique.icon}
                </span>
                <span
                  className="text-[11px] tracking-[0.35em] uppercase"
                  style={{ color: boutique.accent }}
                >
                  Boutique {String(index + 1).padStart(2, "0")} ·{" "}
                  {boutique.countLabel}
                </span>
              </div>
              <h3 className="font-display text-cream text-4xl md:text-5xl leading-[1.05]">
                {boutique.name}
              </h3>
              <p className="mt-2 font-serif italic text-cream/80 text-lg">
                {boutique.subtitle}
              </p>
            </div>
          </div>

          {/* Below banner: description + sort */}
          <div className="p-6 md:p-8 bg-ink">
            <div className="grid md:grid-cols-12 gap-6 items-end">
              <p className="md:col-span-7 text-cream/70 text-base leading-relaxed">
                {boutique.description}
              </p>
              <div className="md:col-span-5 flex md:justify-end items-end">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-cream/40 text-[10px] tracking-[0.25em] uppercase mr-2">
                    Trier
                  </span>
                  {(
                    [
                      ["default", "Curatèle"],
                      ["price-asc", "Prix ↑"],
                      ["price-desc", "Prix ↓"],
                      ["name", "A → Z"],
                    ] as const
                  ).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => setSort(key)}
                      className={`px-3 py-1.5 rounded-full text-[11px] tracking-[0.15em] uppercase border transition-all ${
                        sort === key
                          ? "bg-cream text-ink border-cream"
                          : "border-cream/20 text-cream/80 hover:border-cream/50 hover:text-cream"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Products grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {shown.map((p) => (
            <ProductCard
              key={p.id}
              product={p}
              accent={boutique.accent}
              imgSrc={productImage(p)}
            />
          ))}
        </div>

        {/* Load more (only if more than visible) */}
        {visible < sortedProducts.length && (
          <div className="mt-12 flex flex-col items-center gap-4">
            <div className="text-ink/40 text-[11px] tracking-[0.25em] uppercase">
              {visible} / {sortedProducts.length} produits affichés
            </div>
            <button
              onClick={() =>
                setVisible((v) => Math.min(sortedProducts.length, v + 8))
              }
              className="inline-flex items-center gap-3 px-8 py-3.5 rounded-full border border-ink/30 text-ink hover:bg-ink hover:text-cream transition-colors tracking-[0.2em] uppercase text-sm"
            >
              Voir plus de produits
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 5v14"></path>
                <path d="m19 12-7 7-7-7"></path>
              </svg>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

function ProductCard({
  product,
  accent,
  imgSrc,
}: {
  product: Product;
  accent: string;
  imgSrc: string;
}) {
  const { addItem } = useCart();
  return (
    <article className="group relative bg-white border border-ink/8 rounded-xl overflow-hidden hover:luxury-shadow transition-all duration-500 hover:-translate-y-0.5 flex flex-col">
      {/* Image area */}
      <div
        className="relative aspect-[4/5] overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${accent}22 0%, ${accent}08 50%, #ffffff 100%)`,
        }}
      >
        <img
          src={imgSrc}
          alt={product.name}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="eager"
          decoding="async"
          fetchPriority="low"
          onError={(e) => {
            // If the remote image fails, hide the broken <img>.
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
        {/* Boutique-accent color tint overlay — gives each boutique a
            cohesive visual identity despite the generic stock photos. */}
        <div
          className="absolute inset-0 pointer-events-none mix-blend-multiply opacity-25 transition-opacity duration-500 group-hover:opacity-10"
          style={{
            background: `linear-gradient(135deg, ${accent} 0%, ${accent}66 50%, transparent 100%)`,
          }}
        ></div>
        {/* Subtle dark gradient at the bottom for legibility of the origin tag. */}
        <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/30 to-transparent pointer-events-none"></div>
        {/* Tag */}
        <div className="absolute top-3 left-3">
          <span
            className="px-2.5 py-1 rounded-full text-[9px] tracking-[0.18em] uppercase font-medium"
            style={{
              background: "rgba(255,255,255,0.9)",
              color: accent,
              backdropFilter: "blur(8px)",
            }}
          >
            {product.tag}
          </span>
        </div>
        {/* Origin */}
        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-[10px] tracking-[0.15em] uppercase">
          <span className="text-ink/55 font-medium bg-white/80 backdrop-blur px-2 py-0.5 rounded">
            {product.origin}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 flex flex-col p-4">
        <h4 className="font-serif text-ink text-[15px] leading-snug min-h-[2.6rem] line-clamp-2">
          {product.name}
        </h4>
        <p className="mt-1.5 text-ink/45 text-[11px] leading-relaxed line-clamp-2">
          {product.description}
        </p>
        <div className="mt-3 pt-3 border-t border-ink/8 flex items-center justify-between">
          <span className="font-display text-ink text-base">
            {formatPrice(product.price)}
          </span>
          <button
            aria-label={`Ajouter ${product.name} au panier`}
            onClick={() =>
              addItem({
                id: product.id,
                name: product.name,
                price: product.price,
                image: imgSrc,
                origin: product.origin,
                boutiqueName: product.boutiqueName,
              })
            }
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-ink/15 text-ink/70 hover:bg-gold hover:text-ink hover:border-gold transition-all"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 12h14"></path>
              <path d="M12 5v14"></path>
            </svg>
          </button>
        </div>
      </div>
    </article>
  );
}
